import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Save, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Info,
  Calendar,
  Sun,
  Moon,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface HorarioFuncionamento {
  dia: string;
  abertura: string;
  fechamento: string;
  fechado: boolean;
}

interface HorarioConfig {
  horarios: HorarioFuncionamento[];
  created_at?: string;
  updated_at?: string;
}

const HorarioFuncionamentoPage: React.FC = () => {
  const [config, setConfig] = useState<HorarioConfig>({
    horarios: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const diasSemana = [
    { key: 'segunda', nome: 'Segunda-feira' },
    { key: 'terca', nome: 'Terça-feira' },
    { key: 'quarta', nome: 'Quarta-feira' },
    { key: 'quinta', nome: 'Quinta-feira' },
    { key: 'sexta', nome: 'Sexta-feira' },
    { key: 'sabado', nome: 'Sábado' },
    { key: 'domingo', nome: 'Domingo' }
  ];

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      console.log('🔄 Carregando horários do banco de dados...');
      const response = await api.get('/api/horario-funcionamento/config');
      console.log('📅 Horários carregados:', response.data);
      
      if (response.data && response.data.horarios && response.data.horarios.length > 0) {
        setConfig(response.data);
        console.log('✅ Horários carregados com sucesso do banco');
      } else {
        console.log('⚠️ Nenhum horário encontrado no banco');
        setConfig({ horarios: [] });
        toast.error('Nenhum horário configurado. Configure os horários de funcionamento.');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar configurações de horário:', error);
      toast.error('Erro ao carregar configurações do banco de dados');
      setConfig({ horarios: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleHorarioChange = (dia: string, campo: keyof HorarioFuncionamento, valor: string | boolean) => {
    setConfig(prev => ({
      ...prev,
      horarios: prev.horarios.map(horario => 
        horario.dia === dia 
          ? { ...horario, [campo]: valor }
          : horario
      )
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('💾 Salvando horários no banco de dados...', config.horarios);
      
      const response = await api.post('/api/horario-funcionamento/config', {
        horarios: config.horarios
      });
      
      console.log('✅ Resposta do servidor:', response.data);
      toast.success('Horários de funcionamento salvos com sucesso!');
      
      // Recarregar os dados do banco para confirmar
      setTimeout(() => {
        fetchConfig();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Erro ao salvar horários:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const formatarHorario = (horario: string) => {
    if (horario === '00:00') return 'Meia-noite';
    if (horario === '24:00') return 'Meia-noite';
    return horario;
  };

  const formatarHorarioCompleto = (abertura: string, fechamento: string) => {
    const aberturaFormatada = formatarHorario(abertura);
    const fechamentoFormatada = formatarHorario(fechamento);
    
    // Converter para minutos para verificar se vai até o dia seguinte
    const converterParaMinutos = (horario: string) => {
      const [hora, minuto] = horario.split(':').map(Number);
      return hora * 60 + minuto;
    };
    
    const aberturaMinutos = converterParaMinutos(abertura);
    const fechamentoMinutos = converterParaMinutos(fechamento);
    
    // Se fechamento é menor que abertura, significa que vai até o dia seguinte
    if (fechamentoMinutos < aberturaMinutos) {
      return `${aberturaFormatada} às ${fechamentoFormatada} (dia seguinte)`;
    } else {
      return `${aberturaFormatada} às ${fechamentoFormatada}`;
    }
  };

  const getStatusLoja = () => {
    const agora = new Date();
    const diaAtual = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'][agora.getDay()];
    const horaAtual = agora.getHours().toString().padStart(2, '0') + ':' + agora.getMinutes().toString().padStart(2, '0');
    
    const horarioHoje = config.horarios.find(h => h.dia === diaAtual);
    
    if (!horarioHoje || horarioHoje.fechado) {
      return { aberto: false, mensagem: 'Loja fechada hoje' };
    }

    const abertura = horarioHoje.abertura;
    const fechamento = horarioHoje.fechamento;

    let aberto = false;
    
    // Converter horários para minutos para facilitar comparação
    const converterParaMinutos = (horario: string) => {
      const [hora, minuto] = horario.split(':').map(Number);
      return hora * 60 + minuto;
    };

    const horaAtualMinutos = converterParaMinutos(horaAtual);
    const aberturaMinutos = converterParaMinutos(abertura);
    const fechamentoMinutos = converterParaMinutos(fechamento);

    // Se fechamento é menor que abertura, significa que vai até o dia seguinte
    if (fechamentoMinutos < aberturaMinutos) {
      // Exemplo: 14h até 02h = 14h do dia atual até 02h do dia seguinte
      aberto = horaAtualMinutos >= aberturaMinutos || horaAtualMinutos < fechamentoMinutos;
    } else {
      // Horário normal do mesmo dia
      aberto = horaAtualMinutos >= aberturaMinutos && horaAtualMinutos < fechamentoMinutos;
    }

    return { 
      aberto, 
      mensagem: aberto ? 'Loja aberta agora' : 'Loja fechada agora',
      horarioHoje
    };
  };

  const statusLoja = getStatusLoja();

  // Função para adicionar horário inicial se não existir
  const adicionarHorarioInicial = (dia: string) => {
    const horarioExistente = config.horarios.find(h => h.dia === dia);
    if (!horarioExistente) {
      const novoHorario: HorarioFuncionamento = {
        dia: dia,
        abertura: '08:00',
        fechamento: '18:00',
        fechado: true
      };
      setConfig(prev => ({
        ...prev,
        horarios: [...prev.horarios, novoHorario]
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 md:h-64 lg:h-80">
        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 md:gap-3">
              <Clock className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 text-blue-600" />
              Horário de Funcionamento
            </h1>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
              Configure os horários de funcionamento da sua loja para controlar quando os clientes podem fazer pedidos
            </p>
          </div>
        </div>
      </div>

      {/* Status Atual da Loja */}
      <div className={`p-4 md:p-5 lg:p-6 rounded-lg border-2 shadow-lg ${
        statusLoja.aberto 
          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 dark:from-green-900/30 dark:to-emerald-900/30 dark:border-green-700' 
          : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-300 dark:from-red-900/30 dark:to-orange-900/30 dark:border-red-700'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
          <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
            statusLoja.aberto 
              ? 'bg-green-500 shadow-lg shadow-green-500/50' 
              : 'bg-red-500 shadow-lg shadow-red-500/50'
          }`}>
            {statusLoja.aberto ? (
              <CheckCircle className="h-6 w-6 md:h-7 md:w-7 text-white" />
            ) : (
              <AlertCircle className="h-6 w-6 md:h-7 md:w-7 text-white" />
            )}
          </div>
          <div className="flex-1">
            <h3 className={`text-base md:text-lg lg:text-xl font-bold mb-1 ${
              statusLoja.aberto ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
            }`}>
              {statusLoja.mensagem}
            </h3>
            {statusLoja.horarioHoje && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <p className={`text-xs md:text-sm font-medium ${
                  statusLoja.aberto ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                }`}>
                  Hoje: {formatarHorarioCompleto(statusLoja.horarioHoje.abertura, statusLoja.horarioHoje.fechamento)}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  statusLoja.aberto 
                    ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' 
                    : 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                }`}>
                  {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Formulário de Horários */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <Calendar className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white">
            Configurar Horários por Dia da Semana
          </h3>
        </div>

        <div className="space-y-3 md:space-y-4">
          {diasSemana.map((dia) => {
            const horario = config.horarios.find(h => h.dia === dia.key);
            const horarioExiste = !!horario;
            
            // Se não existe, criar um placeholder
            if (!horarioExiste) {
              return (
                <div key={dia.key} className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-3 md:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                        <Calendar className="h-4 w-4 md:h-5 md:w-5 text-gray-500" />
                      </div>
                      <h4 className="text-sm md:text-base font-medium text-gray-900 dark:text-white">
                        {dia.nome}
                      </h4>
                    </div>
                    <button
                      onClick={() => adicionarHorarioInicial(dia.key)}
                      className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      Adicionar Horário
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div key={dia.key} className={`border-2 rounded-lg p-3 md:p-4 lg:p-5 transition-all ${
                !horario.fechado 
                  ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' 
                  : 'border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50'
              }`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4 mb-3 md:mb-4">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center ${
                      !horario.fechado 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      <Calendar className="h-5 w-5 md:h-6 md:w-6" />
                    </div>
                    <div>
                      <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">
                        {dia.nome}
                      </h4>
                      {!horario.fechado && (
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                          {formatarHorarioCompleto(horario.abertura, horario.fechamento)}
                        </p>
                      )}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 md:gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={!horario.fechado}
                        onChange={(e) => handleHorarioChange(dia.key, 'fechado', !e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 md:w-14 md:h-7 rounded-full transition-colors duration-200 ${
                        !horario.fechado ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        <div className={`w-5 h-5 md:w-6 md:h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                          !horario.fechado ? 'translate-x-6 md:translate-x-7' : 'translate-x-0.5'
                        }`}></div>
                      </div>
                    </div>
                    <span className={`text-xs md:text-sm font-medium ${
                      !horario.fechado 
                        ? 'text-green-700 dark:text-green-300' 
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {!horario.fechado ? 'Aberto' : 'Fechado'}
                    </span>
                  </label>
                </div>

                {!horario.fechado && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    <div className="space-y-1.5 md:space-y-2">
                      <label className="flex items-center gap-2 text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                        <Sun className="h-3.5 w-3.5 md:h-4 md:w-4 text-yellow-500" />
                        Horário de Abertura
                      </label>
                      <input
                        type="time"
                        value={horario.abertura}
                        onChange={(e) => handleHorarioChange(dia.key, 'abertura', e.target.value)}
                        className="w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      />
                    </div>
                    <div className="space-y-1.5 md:space-y-2">
                      <label className="flex items-center gap-2 text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                        <Moon className="h-3.5 w-3.5 md:h-4 md:w-4 text-indigo-500" />
                        Horário de Fechamento
                      </label>
                      <input
                        type="time"
                        value={horario.fechamento}
                        onChange={(e) => handleHorarioChange(dia.key, 'fechamento', e.target.value)}
                        className="w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      />
                      {/* Indicador de horário que vai até o dia seguinte */}
                      {(() => {
                        const converterParaMinutos = (horario: string) => {
                          const [hora, minuto] = horario.split(':').map(Number);
                          return hora * 60 + minuto;
                        };
                        const aberturaMinutos = converterParaMinutos(horario.abertura);
                        const fechamentoMinutos = converterParaMinutos(horario.fechamento);
                        return fechamentoMinutos < aberturaMinutos;
                      })() && (
                        <div className="mt-2 p-2 md:p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                          <p className="text-xs md:text-sm text-blue-700 dark:text-blue-300 flex items-center gap-1.5 md:gap-2">
                            <Clock className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                            <span>Este horário vai até o dia seguinte</span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {horario.fechado && (
                  <div className="text-center py-3 md:py-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 font-medium">
                      Loja fechada neste dia
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Botão Salvar */}
        <div className="mt-4 md:mt-6 flex flex-col sm:flex-row justify-end gap-2 md:gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 md:px-6 md:py-3 border border-transparent text-sm md:text-base font-medium rounded-lg shadow-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 md:h-5 md:w-5" />
                Salvar Horários
              </>
            )}
          </button>
        </div>
      </div>

      {/* Informações Adicionais */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex items-start gap-3 md:gap-4">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
            <Info className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm md:text-base lg:text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2 md:mb-3">
              Como funciona o horário de funcionamento?
            </h4>
            <ul className="text-xs md:text-sm text-blue-800 dark:text-blue-200 space-y-1.5 md:space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold mt-0.5">•</span>
                <span>Os clientes só poderão fazer pedidos quando a loja estiver aberta</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold mt-0.5">•</span>
                <span>O horário é verificado automaticamente em tempo real</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold mt-0.5">•</span>
                <span><strong>Horários que cruzam a meia-noite:</strong> Se você colocar 14h até 02h, o sistema entende que é 14h do dia atual até 02h da madrugada do dia seguinte</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold mt-0.5">•</span>
                <span><strong>Exemplo:</strong> Quinta-feira 14h às 02h = abre quinta 14h e fecha sexta 02h</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold mt-0.5">•</span>
                <span>Os horários são aplicados imediatamente após salvar</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorarioFuncionamentoPage;
